import React, { useEffect, useState } from "react";
import { Table, Button, Modal, Form, Input, Select, DatePicker, Space, message, Calendar, Tooltip } from "antd";
import { CheckOutlined, CloseOutlined, QuestionCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import axios from "axios";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import PageHeader from "../../components/ui/PageHeader";
import PageTransition from "../../components/ui/PageTransition";
import EmptyState from "../../components/ui/EmptyState";
import StatusTag from "../../components/ui/StatusTag";
import ViewToggle from "../../components/ui/ViewToggle";

const { RangePicker } = DatePicker;

// embedded: rendered inside the Leave page tabs, so no page header of its own.
const LeaveRequests = ({ embedded }) => {
  const { request } = useRequest();
  const [requests, setRequests] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filters, setFilters] = useState({ status: undefined, department: undefined, from: undefined, to: undefined });
  const [view, setView] = useState("list");

  const [actionModal, setActionModal] = useState({ open: false, type: null, record: null });
  const [form] = Form.useForm();

  const fetchRequests = () => {
    const params = new URLSearchParams();
    if (filters.status) params.append("status", filters.status);
    if (filters.department) params.append("department", filters.department);
    if (filters.from) params.append("from", filters.from);
    if (filters.to) params.append("to", filters.to);
    request({
      url: `${apiPath.leaveRequests}?${params.toString()}`,
      method: "GET",
      onSuccess: (res) => setRequests(res?.data?.data || []),
    });
  };

  const fetchDepartments = () => {
    request({ url: apiPath.departments, method: "GET", onSuccess: (res) => setDepartments(res?.data?.data || []) });
  };

  useEffect(() => {
    fetchDepartments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const openAction = (type, record) => {
    form.resetFields();
    setActionModal({ open: true, type, record });
  };

  const onDownloadDocument = async (record) => {
    try {
      const token = localStorage.getItem("i_ems_admin_token") || "";
      const response = await axios.get(`${apiPath.baseURL}${apiPath.leaveRequestDocumentDownload(record._id)}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", record.supportingDocumentFileName || "document");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Could not download document");
    }
  };

  const onSubmitAction = () => {
    form.validateFields().then((values) => {
      const { type, record } = actionModal;
      const urlMap = {
        approve: `${apiPath.leaveRequests}/${record._id}/approve`,
        reject: `${apiPath.leaveRequests}/${record._id}/reject`,
        clarify: `${apiPath.leaveRequests}/${record._id}/clarify`,
      };
      request({
        url: urlMap[type],
        method: "PUT",
        data: type === "clarify" ? { clarificationNote: values.comment } : { adminComment: values.comment },
        onSuccess: () => {
          message.success("Leave request updated");
          setActionModal({ open: false, type: null, record: null });
          fetchRequests();
        },
      });
    });
  };

  const leaveStatusColors = { pending: "#F59E0B", approved: "#14B8A6", rejected: "#EF4444" };

  const dateCellRender = (value) => {
    const dateStr = value.format("YYYY-MM-DD");
    const matches = requests.filter((r) => r.startDate <= dateStr && r.endDate >= dateStr);
    if (!matches.length) return null;
    return (
      <div>
        {matches.map((r) => {
          const label = `${r.employee?.name || "Unknown"} — ${r.leaveType?.name || "Leave"}`;
          const color = leaveStatusColors[r.status] || "#9CA3AF";
          return (
            <Tooltip key={r._id} title={label}>
              <div
                style={{
                  fontSize: 11,
                  lineHeight: "16px",
                  color,
                  background: `${color}1A`,
                  borderRadius: 3,
                  padding: "0 4px",
                  marginBottom: 2,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {label}
              </div>
            </Tooltip>
          );
        })}
      </div>
    );
  };

  const columns = [
    { title: "Employee", dataIndex: ["employee", "name"] },
    { title: "Department", dataIndex: ["employee", "department", "name"] },
    { title: "Leave Type", dataIndex: ["leaveType", "name"] },
    { title: "From", dataIndex: "startDate" },
    { title: "To", dataIndex: "endDate" },
    { title: "Days", dataIndex: "days" },
    { title: "Reason", dataIndex: "reason", ellipsis: true },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => <StatusTag status={status} />,
    },
    { title: "Comment", dataIndex: "adminComment" },
    {
      title: "Actions",
      render: (_, record) => (
        <Space>
          {record.supportingDocumentKey && (
            <Button size="small" icon={<DownloadOutlined />} onClick={() => onDownloadDocument(record)}>
              Document
            </Button>
          )}
          {record.status === "pending" && (
            <>
              <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => openAction("approve", record)}>
                Approve
              </Button>
              <Button size="small" danger icon={<CloseOutlined />} onClick={() => openAction("reject", record)}>
                Reject
              </Button>
              <Button size="small" icon={<QuestionCircleOutlined />} onClick={() => openAction("clarify", record)}>
                Clarify
              </Button>
            </>
          )}
        </Space>
      ),
    },
  ];

  const Wrapper = embedded ? React.Fragment : PageTransition;

  return (
    <Wrapper>
      {embedded ? null : (
        <PageHeader
          title="Leave Requests"
          subtitle="Review, approve, reject or request clarification on leave requests"
          actions={<ViewToggle value={view} onChange={setView} />}
        />
      )}
      <Space style={{ marginBottom: 16 }} wrap>
        {embedded ? <ViewToggle value={view} onChange={setView} /> : null}
        <Select
          allowClear
          placeholder="Status"
          style={{ width: 150 }}
          value={filters.status}
          onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
          options={[
            { value: "pending", label: "Pending" },
            { value: "approved", label: "Approved" },
            { value: "rejected", label: "Rejected" },
          ]}
        />
        <Select
          allowClear
          placeholder="Department"
          style={{ width: 200 }}
          value={filters.department}
          onChange={(v) => setFilters((f) => ({ ...f, department: v }))}
          options={departments.map((d) => ({ value: d._id, label: d.name }))}
        />
        <RangePicker
          onChange={(dates, dateStrings) => setFilters((f) => ({ ...f, from: dateStrings[0] || undefined, to: dateStrings[1] || undefined }))}
        />
      </Space>
      {view === "list" ? (
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={requests}
          pagination={{ pageSize: 10 }}
          locale={{ emptyText: <EmptyState message="No leave requests found" /> }}
        />
      ) : (
        <Calendar dateCellRender={dateCellRender} />
      )}

      <Modal
        title={actionModal.type === "approve" ? "Approve Leave Request" : actionModal.type === "reject" ? "Reject Leave Request" : "Request Clarification"}
        open={actionModal.open}
        onCancel={() => setActionModal({ open: false, type: null, record: null })}
        onOk={onSubmitAction}
        destroyOnClose
      >
        <Form layout="vertical" form={form}>
          <Form.Item
            name="comment"
            label={actionModal.type === "clarify" ? "Clarification note" : "Comment (optional)"}
            rules={actionModal.type === "clarify" ? [{ required: true, message: "Please enter a clarification note" }] : []}
          >
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>
    </Wrapper>
  );
};

export default LeaveRequests;
